import { useCallback, useEffect, useState, memo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate, vehicleLabel as vehLabelFn } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import SaleFormDrawer from "@/components/SaleFormDrawer";
import InvoiceModal from "@/components/InvoiceModal";
import BranchFilter from "@/components/BranchFilter";
import RelatedVouchers from "@/components/RelatedVouchers";
import SaleApprovalActions, { ApprovalStatusBadge, PrintSaleReceiptButton } from "@/components/SaleApprovalActions";
import ReceiptPrintMenu from "@/components/ReceiptPrintMenu";
import PrintDeliveryChallanButton from "@/components/PrintDeliveryChallanButton";
import StartRegistrationButton from "@/components/StartRegistrationButton";
import SaleDetailDrawer from "@/components/SaleDetailDrawer";
import ModelCategoryFilter from "@/components/ModelCategoryFilter";
import DateRangeFilter from "@/components/DateRangeFilter";
import { useAuth } from "@/context/AuthContext";
import { Plus, Receipt, CaretDown, CaretRight, MagnifyingGlass, X, Eye, Stack } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

function SaleRowImpl({ sale, isAdmin, user, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  // Prefer the snapshot of attached package names (e.g. "JUPITER 125 BSVI-DISC")
  // — that's the operationally meaningful variant string the dealer cares
  // about. Fall back to the hydrated package_name when no packages snapshot
  // was attached (rare — usually only on legacy migrated sales).
  const packageNames = (sale.packages || [])
    .map((p) => p.name)
    .filter(Boolean);
  const vehicleLabel = packageNames.length
    ? packageNames.join(" · ")
    : vehLabelFn(sale.vehicle) || "—";
  const dueValue = Number(sale.due) || 0;
  const writeoffValue = Number(sale.writeoff_amount) || 0;
  const dueClass =
    dueValue > 0
      ? "text-destructive"
      : dueValue < 0
        ? "text-amber-600"
        : "text-[#00B341]";
  // "Due / Write-off" — shows both numbers so verifiers can see split
  // dispositions at a glance (e.g. 2000/1000 = 2000 outstanding + 1000
  // already written off). Sign preserved for excess (negative due).
  const dueDisplay = dueValue === 0 ? "0" : `${dueValue > 0 ? "" : "−"}${fmtMoney(Math.abs(dueValue))}`;
  const woDisplay = writeoffValue > 0 ? fmtMoney(writeoffValue) : "0";
  return (
    <>
      <tr
        data-testid={`sale-row-${sale.id}`}
        className="border-b border-canvas-line hover:bg-canvas-panel transition-colors"
      >
        <td className="px-2 py-3">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            data-testid={`toggle-sale-${sale.id}`}
            className="p-1 border border-canvas-line hover:border-ink"
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? (
              <CaretDown size={11} weight="bold" />
            ) : (
              <CaretRight size={11} weight="bold" />
            )}
          </button>
        </td>
        <td className="px-3 py-3 whitespace-nowrap font-mono text-[11px] text-ink">{fmtDate(sale.sale_date)}</td>
        <td className="px-3 py-3">
          <div className="text-sm font-semibold text-ink whitespace-nowrap">{vehicleLabel}</div>
          <div className="font-mono text-[11px] text-ink-muted truncate max-w-[180px]">{sale.vehicle?.vin}</div>
        </td>
        <td className="px-3 py-3 max-w-[180px]">
          <div className="text-sm text-ink truncate">{sale.customer?.name || "—"}</div>
          <div className="font-mono text-[11px] text-ink-muted truncate">{sale.customer?.email}</div>
        </td>
        <td className="px-3 py-3 text-sm text-ink whitespace-nowrap">
          {sale.salesperson_name}
          {sale.sales_agent_name && (
            <div className="font-mono text-[10px] text-ink-muted mt-0.5 uppercase tracking-wider whitespace-nowrap">
              Agent · {sale.sales_agent_name}
            </div>
          )}
        </td>
        <td className="px-3 py-3 whitespace-nowrap">
          <span className="inline-flex items-center px-2 py-1 border border-canvas-line text-[11px] font-mono uppercase tracking-wider">
            {sale.payment_method}
          </span>
        </td>
        <td className="px-3 py-3 whitespace-nowrap">
          {(() => {
            // Sale-type badge (distinct from payment method). Priority:
            // LIEN > FIN+EX > FINANCE > EXCHANGE > CASH.
            const hasFin = !!sale.has_finance || ["finance", "exchange_finance"].includes(sale.sale_type);
            const hasEx = !!sale.has_exchange || ["exchange", "exchange_finance"].includes(sale.sale_type);
            const finType = (sale.finance?.finance_type || "loan").toLowerCase();
            const isLien = hasFin && finType === "lien";
            let label = "CASH";
            let cls = "border-slate-300 bg-slate-100 text-slate-700";
            if (isLien) {
              label = "LIEN";
              cls = "border-purple-500 bg-purple-100 text-purple-800";
            } else if (hasFin && hasEx) {
              label = "FIN+EX";
              cls = "border-indigo-500 bg-indigo-100 text-indigo-800";
            } else if (hasFin) {
              label = "FINANCE";
              cls = "border-red-500 bg-red-100 text-red-800";
            } else if (hasEx) {
              label = "EXCHANGE";
              cls = "border-emerald-500 bg-emerald-100 text-emerald-800";
            }
            return (
              <span
                className={`inline-flex items-center px-2 py-1 border text-[11px] font-mono uppercase tracking-wider ${cls}`}
                data-testid={`sale-type-badge-${sale.id}`}
              >
                {label}
              </span>
            );
          })()}
        </td>
        <td className="px-3 py-3 whitespace-nowrap font-mono text-sm font-semibold text-ink">
          {fmtMoney(sale.sale_price)}
        </td>
        <td
          className={`px-3 py-3 whitespace-nowrap font-mono text-sm font-semibold tabular-nums ${dueClass}`}
          data-testid={`sale-due-${sale.id}`}
          title={writeoffValue > 0 ? `Due ${dueDisplay} · Write-off ${woDisplay}` : `Due ${dueDisplay}`}
        >
          {dueDisplay}
          <span className="text-ink-muted">/</span>
          <span className={writeoffValue > 0 ? "text-amber-700" : "text-ink-muted"}>{woDisplay}</span>
        </td>
        <td className="px-3 py-3 whitespace-nowrap">
          <ApprovalStatusBadge status={sale.approval_status} />
        </td>
        <td className="px-3 py-3 text-right whitespace-nowrap">
          <div className="flex items-center justify-end gap-1 flex-nowrap">
            <button
              type="button"
              onClick={() => setShowDetail(true)}
              data-testid={`view-sale-${sale.id}`}
              title="View submitted sale form (read-only)"
              className="p-1.5 border border-canvas-line hover:border-ink inline-flex items-center justify-center"
            >
              <Eye size={12} weight="bold" />
            </button>
            <ReceiptPrintMenu sale={sale} />
            <PrintDeliveryChallanButton sale={sale} />
            <PrintSaleReceiptButton sale={sale} />
            {/* Reconcile-due removed — dues are now collected from
                Reports → Retail Dues where the Collect flow lives. */}
            <StartRegistrationButton sale={sale} user={user} onStarted={onChanged} />
            <SaleApprovalActions sale={sale} isAdmin={isAdmin} onChanged={onChanged} />
          </div>
        </td>
      </tr>
      {expanded && (
        <tr
          data-testid={`sale-detail-${sale.id}`}
          className="border-b border-canvas-line bg-canvas-panel/30"
        >
          <td colSpan={11} className="px-4 py-3">
            <RelatedVouchers context="sale" record={sale} defaultCollapsed={false} />
          </td>
        </tr>
      )}
      <SaleDetailDrawer open={showDetail} sale={sale} onClose={() => setShowDetail(false)} />
    </>
  );
}

// Memoize so a single-row patch (Reconcile / Approve) doesn't re-render the
// other 4,000+ rows. Unchanged rows keep the same `sale` reference under
// .map(), so `memo`'s default shallow-compare correctly skips them.
const SaleRow = memo(SaleRowImpl);

function EmptyState({ onAdd }) {
  return (
    <tr>
      <td colSpan={11} className="px-4 py-12 text-center">
        <Receipt size={24} weight="bold" className="text-ink-subtle mx-auto mb-2" />
        <div className="font-mono text-xs text-ink-subtle mb-3">No sales recorded yet.</div>
        <button onClick={onAdd} className="text-sm font-semibold text-brand hover:underline">
          + Record your first sale
        </button>
      </td>
    </tr>
  );
}

const STATUS_FILTERS = [
  { v: "", l: "Active" },
  { v: "pending_approval", l: "Pending" },
  { v: "approved", l: "Verified" },
  { v: "rejected", l: "Rejected" },
];

export default function SalesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sales, setSales] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [agents, setAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  // Default date filter: last 30 days. Operators can widen via the preset
  // dropdown or clear it entirely. Stored as YYYY-MM-DD strings.
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(today); start.setDate(today.getDate() - 29);
    const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    return { from: ymd(start), to: ymd(today) };
  });
  // Server-side pagination — we ask the backend for one page at a time
  // and grow the visible window by hitting the API again. Prevents payload
  // bloat on multi-year sale histories and lets Mongo do the sort/slice.
  const PAGE_SIZE = 100;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [totalSales, setTotalSales] = useState(0);
  const [salesLoadingMore, setSalesLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/sales-agents").then(({ data }) => {
      if (!cancelled) setAgents(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const salesParams = { limit: PAGE_SIZE, skip: 0 };
      if (branchFilter) salesParams.branch_id = branchFilter;
      if (statusFilter) {
        salesParams.approval_status = statusFilter;
        if (statusFilter === "rejected") salesParams.include_rejected = true;
      }
      if (searchQuery) salesParams.search = searchQuery;
      if (agentFilter) salesParams.sales_agent_id = agentFilter;
      if (modelFilter) salesParams.model_category = modelFilter;
      if (dateRange.from) salesParams.date_from = dateRange.from;
      if (dateRange.to) salesParams.date_to = dateRange.to;
      const vehParams = { status: "available", ...(branchFilter ? { branch_id: branchFilter } : {}) };
      const custParams = { active_only: true, ...(branchFilter ? { branch_id: branchFilter } : {}) };
      const [s, v, c] = await Promise.all([
        api.get("/sales", { params: salesParams }),
        api.get("/vehicles", { params: vehParams }),
        api.get("/customers", { params: custParams }),
      ]);
      setSales(s.data);
      const total = Number(s.headers?.["x-total-count"]) || s.data.length;
      setTotalSales(total);
      setVisibleCount(s.data.length);
      setVehicles(v.data);
      setCustomers(c.data);
    } finally {
      setLoading(false);
    }
  }, [branchFilter, statusFilter, searchQuery, agentFilter, modelFilter, dateRange.from, dateRange.to]);

  const loadMoreSales = useCallback(async () => {
    if (salesLoadingMore) return;
    setSalesLoadingMore(true);
    try {
      const salesParams = { limit: PAGE_SIZE, skip: visibleCount };
      if (branchFilter) salesParams.branch_id = branchFilter;
      if (statusFilter) {
        salesParams.approval_status = statusFilter;
        if (statusFilter === "rejected") salesParams.include_rejected = true;
      }
      if (searchQuery) salesParams.search = searchQuery;
      if (agentFilter) salesParams.sales_agent_id = agentFilter;
      if (modelFilter) salesParams.model_category = modelFilter;
      if (dateRange.from) salesParams.date_from = dateRange.from;
      if (dateRange.to) salesParams.date_to = dateRange.to;
      const s = await api.get("/sales", { params: salesParams });
      setSales((prev) => [...prev, ...s.data]);
      setVisibleCount((c) => c + s.data.length);
      const total = Number(s.headers?.["x-total-count"]);
      if (Number.isFinite(total)) setTotalSales(total);
    } finally {
      setSalesLoadingMore(false);
    }
  }, [visibleCount, branchFilter, statusFilter, searchQuery, agentFilter, modelFilter, dateRange.from, dateRange.to, salesLoadingMore]);

  useEffect(() => {
    // Reset the page window whenever filters change so we don't preserve
    // stale offsets after a filter narrows the result set.
    setVisibleCount(PAGE_SIZE);
  }, [branchFilter, statusFilter, searchQuery, agentFilter, modelFilter, dateRange.from, dateRange.to]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaved = (newInvoice) => {
    setShowForm(false);
    setInvoice(newInvoice);
    // After a successful sale submit we used to refetch every list
    // (`/sales` + `/vehicles?available` + `/customers?active_only`), which on
    // this dataset is ~10 MB of hydrated rows and made the browser hang for
    // minutes. Instead, splice the newly-created sale into the top of the
    // local list. The just-reserved vehicle drops out of the "available" list
    // lazily on next reload (e.g. on filter change) — acceptable trade-off
    // for sub-second submit feedback.
    if (newInvoice && newInvoice.id) {
      setSales((prev) => {
        const existing = prev.find((s) => s.id === newInvoice.id);
        if (existing) {
          return prev.map((s) => (s.id === newInvoice.id ? { ...s, ...newInvoice } : s));
        }
        return [newInvoice, ...prev];
      });
      // Drop the reserved vehicle from the available-vehicles cache so the
      // SaleFormDrawer's vehicle picker no longer offers it.
      if (newInvoice.vehicle_id) {
        setVehicles((prev) => prev.filter((v) => v.id !== newInvoice.vehicle_id));
      }
    }
  };

  // Optimistic single-row patcher used after Reconcile / Start-Reg / Approve.
  // The reconcile endpoint returns the updated sale, so we can splice it
  // straight into local state — avoiding a 4,000-row refetch + re-render
  // that was making the page freeze for several seconds.
  // Callers that don't pass back the updated sale (Approve/Reject, Start-Reg
  // which returns a registration not a sale, or any payload whose id we
  // don't recognise) get the safe fallback: a full reload.
  const onRowChanged = useCallback((payload) => {
    if (payload && payload.id) {
      // Use the functional-update form so we work off the freshest sales list,
      // but compute the existence check up front (no side-effect inside the
      // updater — React may call it twice in dev under StrictMode).
      let found = false;
      setSales((prev) => {
        const hit = prev.find((s) => s.id === payload.id);
        if (!hit) return prev;
        found = true;
        return prev.map((s) => (s.id === payload.id ? { ...s, ...payload } : s));
      });
      // Note: `found` reflects the last invocation only — that's fine because
      // either way we settle into the same state. If the row was patched we
      // skip the network reload; otherwise we fall through to the safe reload
      // (used by StartRegistrationButton which returns a registration, not a
      // sale).
      if (found) return;
    }
    load();
  }, [load]);

  return (
    <div>
      <PageHeader
        eyebrow="02 / Sales"
        title="Sales Ledger"
        subtitle="Record transactions, generate invoices, and update stock in one motion. New sales enter Pending Approval; admins finalise to mark the vehicle sold."
        actions={
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={async () => {
                  if (!window.confirm("Renumber DCs chronologically across all branches?\n\nThis reassigns DC numbers for every non-rejected sale in date order (oldest → newest). Existing reprints will show the new number.")) return;
                  try {
                    const { data } = await api.post("/sales/admin/reindex-dc-numbers", { dry_run: false });
                    toast.success(`Renumbered ${data.updated} sales across ${Object.keys(data.plan).length} branch/FY buckets`);
                  } catch (err) {
                    toast.error(formatApiError(err));
                  }
                }}
                data-testid="dc-reindex-btn"
                className="border border-canvas-line text-ink-muted px-3 py-2.5 text-xs font-mono uppercase tracking-wider hover:border-ink hover:text-ink"
                title="Renumber DCs chronologically by sale_date (admin)"
              >
                Renumber DCs
              </button>
            )}
            <Link
              to="/sales/bulk"
              data-testid="bulk-sale-link"
              className="border border-ink text-ink px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-ink hover:text-white transition-colors duration-150 flex items-center gap-2"
            >
              <Stack size={14} weight="bold" />
              Bulk Sale
            </Link>
            <button
              onClick={() => setShowForm(true)}
              data-testid="new-sale-button"
              className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
            >
              <Plus size={14} weight="bold" />
              New Sale
            </button>
          </div>
        }
      />

      <div className="px-8 py-6">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex border border-canvas-line" data-testid="sale-status-filter">
            {STATUS_FILTERS.map((opt) => {
              const active = statusFilter === opt.v;
              return (
                <button
                  key={opt.v || "active"}
                  type="button"
                  onClick={() => setStatusFilter(opt.v)}
                  data-testid={`sale-status-filter-${opt.v || "active"}`}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${
                    active ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"
                  }`}
                >
                  {opt.l}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3 flex-1 justify-end flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[220px]">
              <MagnifyingGlass
                size={12}
                weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search VIN, package, customer…"
                data-testid="sales-search"
                className="w-full pl-8 pr-8 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  data-testid="sales-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-canvas-panel"
                  aria-label="Clear search"
                >
                  <X size={11} weight="bold" />
                </button>
              )}
            </div>
            <BranchFilter value={branchFilter} onChange={setBranchFilter} testId="sales-branch-filter" kind="sales" />
            <ModelCategoryFilter value={modelFilter} onChange={setModelFilter} testId="sales-model-filter" />
            <DateRangeFilter value={dateRange} onChange={setDateRange} testIdPrefix="sales-date" />
            <select
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              data-testid="sales-agent-filter"
              className="px-3 py-1.5 bg-canvas border border-canvas-line text-xs font-mono uppercase tracking-wider focus:outline-none focus:border-ink"
            >
              <option value="">All agents</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="sales-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  <th className="w-8 px-2 py-3" />
                  {[
                    "Date",
                    "Vehicle",
                    "Customer",
                    "Salesperson",
                    "Payment",
                    "Sale Type",
                    "Amount",
                    "Due / Write-off",
                    "Status",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                  <th aria-label="Actions" className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && sales.length === 0 && (
                  <EmptyState onAdd={() => setShowForm(true)} />
                )}
                {sales.slice(0, visibleCount).map((s) => (
                  <SaleRow key={s.id} sale={s} isAdmin={isAdmin} user={user} onChanged={onRowChanged} />
                ))}
                {visibleCount < totalSales && (
                  <tr>
                    <td colSpan={11} className="px-4 py-6 text-center bg-canvas-panel/40">
                      <button
                        type="button"
                        onClick={loadMoreSales}
                        disabled={salesLoadingMore}
                        data-testid="sales-load-more"
                        className="px-4 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:bg-canvas-panel disabled:opacity-50"
                      >
                        {salesLoadingMore
                          ? "Loading…"
                          : `Load more · showing ${visibleCount} of ${totalSales}`}
                      </button>
                    </td>
                  </tr>
                )}
                {!loading && sales.length > 0 && visibleCount >= totalSales && totalSales > PAGE_SIZE && (
                  <tr>
                    <td colSpan={11} className="px-4 py-3 text-center bg-canvas-panel/20 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
                      End of ledger · {totalSales} sales
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <SaleFormDrawer
        open={showForm}
        vehicles={vehicles}
        customers={customers}
        onClose={() => setShowForm(false)}
        onSaved={handleSaved}
      />

      <InvoiceModal invoice={invoice} onClose={() => setInvoice(null)} />
    </div>
  );
}
